import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import GoldSiverHeader from '../HomePage/GoldSiverHeader';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getProfile } from '../../store/auth';

const Achievements = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(true);

    const user = useSelector(state => state.auth.user_data);

    useEffect(() => {
        fetchAchievements();
        // Trigger setup if empty (dev helper, usually removed in prod)
        // axios.get('/api/api/setup_achievements.php'); 
    }, []);

    const fetchAchievements = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/api/achievements.php');
            if (response.data.success) {
                setAchievements(response.data.achievements);
            }
        } catch (error) {
            console.error('Failed to fetch achievements', error);
            // toast.error(t('Failed to load achievements'));
        } finally {
            setLoading(false);
        }
    };

    const claimReward = async (achievementId) => {
        try {
            const response = await axios.post('/api/api/achievements.php', { achievementId });
            if (response.data.success) {
                toast.success(`${t('Reward Claimed')}: ${response.data.reward.amount} ${t(response.data.reward.type)}`);
                fetchAchievements(); // Refresh list
                dispatch(getProfile()); // Refresh user balance
            }
        } catch (error) {
            toast.error(t('Failed to claim reward'));
        }
    };

    const styles = {
        ar_achievements_section: {
            padding: '50px 0'
        },
        ar_achievements_wrapper: {
            background: 'url(/images/pokedex/bg-content.png) no-repeat scroll 0 0 / 100% 100%',
            padding: '40px',
            borderRadius: '15px',
            height: '800px', // Fixed height
            overflowY: 'auto', // Enable vertical scrolling
            scrollbarWidth: 'thin', // Firefox
            direction: 'rtl' // Ensure scrollbar is on the correct side for Hebrew
        },
        ar_achievement_row: {
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid #444',
            borderRadius: '10px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '15px',
            transition: 'all 0.3s',
            position: 'relative'
        },
        ar_ach_icon: {
            width: '60px',
            height: '60px',
            background: '#222',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '20px',
            border: '2px solid #555',
            fontSize: '24px'
        },
        ar_ach_icon_completed: {
            borderColor: '#ffd700',
            background: 'rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 10px rgba(255, 215, 0, 0.3)'
        },
        ar_ach_content: {
            flex: 1,
            color: '#fff'
        },
        ar_ach_title: {
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '5px',
            color: '#fff'
        },
        ar_ach_desc: {
            fontSize: '15px',
            color: '#ddd'
        },
        badge_pending: {
            backgroundColor: '#555',
            color: '#fff',
            padding: '8px 15px',
            borderRadius: '20px',
            fontSize: '14px'
        },
        badge_completed: {
            backgroundColor: '#28a745',
            color: '#fff',
            padding: '8px 15px',
            borderRadius: '20px',
            fontSize: '14px',
            boxShadow: '0 0 5px rgba(40, 167, 69, 0.5)'
        },
        badge_claim: {
            backgroundColor: '#ffd700',
            color: '#000',
            padding: '8px 15px',
            borderRadius: '20px',
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: 'bold',
            border: 'none'
        },
        progressBarContainer: {
            width: '100%',
            backgroundColor: '#333',
            borderRadius: '5px',
            height: '5px',
            marginTop: '10px'
        },
        progressBar: {
            height: '100%',
            backgroundColor: '#4a90e2',
            borderRadius: '5px',
            transition: 'width 0.5s'
        }
    };

    return (
        <div className="ar_mainBody">
            <GoldSiverHeader previous="/home" title={t('Achievements')} />

            <section style={styles.ar_achievements_section}>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <div style={styles.ar_achievements_wrapper}>
                                <div className="row justify-content-center">
                                    <div className="col-md-10">
                                        
                                        {achievements.map(ach => {
                                            const isCompleted = ach.completed;
                                            const isClaimed = ach.claimed;
                                            const progressPercent = Math.min(100, Math.round((ach.progress / ach.target_value) * 100));
                                            
                                            // Icon mapping (simplified)
                                            let iconClass = 'fa fa-trophy';
                                            if (ach.icon === 'pokeball') iconClass = 'fa fa-dot-circle';
                                            if (ach.icon === 'fist') iconClass = 'fa fa-fist-raised';
                                            if (ach.icon === 'swords') iconClass = 'fa fa-crosshairs';
                                            if (ach.icon === 'medal' || ach.icon === 'badge') iconClass = 'fa fa-medal';
                                            
                                            return (
                                                <div key={ach.id} style={styles.ar_achievement_row}>
                                                    <div style={{
                                                        ...styles.ar_ach_icon, 
                                                        ...(isCompleted ? styles.ar_ach_icon_completed : {})
                                                    }}>
                                                        <i className={iconClass} style={{color: isCompleted ? '#ffd700' : '#bbb'}}></i>
                                                    </div>
                                                    <div style={styles.ar_ach_content}>
                                                        <div style={styles.ar_ach_title}>{t(ach.name_key)}</div>
                                                        <div style={styles.ar_ach_desc}>{t(ach.description_key)}</div>
                                                        <div style={styles.progressBarContainer}>
                                                            <div style={{...styles.progressBar, width: `${progressPercent}%`, backgroundColor: isCompleted ? '#28a745' : '#4a90e2'}}></div>
                                                        </div>
                                                        <small>{ach.progress} / {ach.target_value} {t(ach.type)}</small>
                                                    </div>
                                                    <div style={{marginLeft: '15px'}}>
                                                        {isCompleted && !isClaimed ? (
                                                            <button 
                                                                style={styles.badge_claim}
                                                                onClick={() => claimReward(ach.id)}
                                                            >
                                                                {t('Claim Reward')}
                                                            </button>
                                                        ) : isClaimed ? (
                                                            <span style={styles.badge_completed}>{t('Completed')}</span>
                                                        ) : (
                                                            <span style={styles.badge_pending}>{progressPercent}%</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {achievements.length === 0 && !loading && (
                                            <div className="text-center text-white">
                                                <h3>{t('No achievements found')}</h3>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Achievements;
